import React from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { getBrand } from '../config/brands';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${props => props.theme.secondaryColor} 0%, #c3cfe2 100%);
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;

  .logo {
    font-size: 1.5rem;
    font-weight: bold;
    color: ${props => props.theme.primaryColor};
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const ContentSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 2rem;
  background: white;
  margin-top: 2rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${props => props.theme.primaryColor};
    margin-bottom: 1rem;
    margin-top: 2rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;
  }

  p {
    color: #4b5563;
    line-height: 1.8;
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 1.5rem;
    margin-bottom: 1rem;

    li {
      color: #4b5563;
      line-height: 1.8;
      margin-bottom: 0.5rem;
    }
  }
`;

const FooterSection = styled.footer`
  background: #1f2937;
  color: white;
  padding: 2rem;
  text-align: center;
`;

interface PrivacyPageProps {}

const PrivacyPage: React.FC<PrivacyPageProps> = () => {
  const [searchParams] = useSearchParams();
  const { brand: brandParam } = useParams<{ brand?: string }>();
  // Use brand from URL parameter if present, otherwise use ref query parameter
  const refParam = brandParam || searchParams.get('ref');
  const brand = getBrand(refParam || undefined);

  const handleLogoClick = () => {
    const landingPath = refParam ? `/${refParam}` : '/';
    window.location.href = `https://finaconnect.com${landingPath}`;
  };

  const theme = {
    primaryColor: brand.primaryColor,
    secondaryColor: brand.secondaryColor
  };

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <Header>
          <HeaderContent>
            <div className="logo" onClick={handleLogoClick}>
              {brand.logo ? (
                <img src={brand.logo} alt={brand.name} style={{ height: '40px', maxWidth: '200px', objectFit: 'contain' }} />
              ) : (
                brand.name
              )}
            </div>
          </HeaderContent>
        </Header>

        <ContentSection>
          <Title>Privacy Policy</Title>
          <Subtitle>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</Subtitle>

          <Section>
            <h2>1. Introduction</h2>
            <p>
              {brand.name} ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you use our merchant onboarding services.
            </p>
          </Section>

          <Section>
            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li>Name, email address, phone number, and contact details</li>
              <li>Business information (company name, business type, tax ID)</li>
              <li>Financial information (bank account details, transaction history)</li>
              <li>Identity verification documents (passport, driver's license, utility bills)</li>
              <li>Business documentation (articles of incorporation, business licenses)</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you use our services, we may automatically collect:</p>
            <ul>
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </Section>

          <Section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Process your merchant onboarding application</li>
              <li>Verify your identity and conduct KYC/AML compliance checks</li>
              <li>Provide and maintain our services</li>
              <li>Communicate with you about your account and services</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Detect and prevent fraud and security threats</li>
              <li>Improve our services and user experience</li>
              <li>Send you marketing communications (with your consent)</li>
            </ul>
          </Section>

          <Section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We may share your information with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf</li>
              <li><strong>Financial Institutions:</strong> Banks and payment processors for account setup and transactions</li>
              <li><strong>Compliance Partners:</strong> KYC/AML verification services and compliance monitoring providers</li>
              <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
            </ul>
            <p>
              We do not sell your personal information to third parties for their marketing purposes.
            </p>
          </Section>

          <Section>
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against
              unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication procedures</li>
              <li>Employee training on data protection</li>
              <li>Secure data storage and backup systems</li>
            </ul>
          </Section>

          <Section>
            <h2>6. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy
              Policy, comply with legal obligations, resolve disputes, and enforce our agreements. Specific retention periods
              may vary based on regulatory requirements and the nature of the information.
            </p>
          </Section>

          <Section>
            <h2>7. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service</li>
              <li><strong>Objection:</strong> Object to processing of your information</li>
              <li><strong>Withdrawal:</strong> Withdraw consent for processing (where applicable)</li>
            </ul>
            <p>
              To exercise these rights, please contact us at{' '}
              <a href={`mailto:${brand.contactEmail}`} style={{ color: brand.primaryColor }}>
                {brand.contactEmail}
              </a>
            </p>
          </Section>

          <Section>
            <h2>8. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to enhance your experience, analyze usage, and personalize
              content. You can control cookie preferences through your browser settings, though disabling cookies may
              affect the functionality of our services.
            </p>
          </Section>

          <Section>
            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence.
              We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy
              and applicable data protection laws.
            </p>
          </Section>

          <Section>
            <h2>10. Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal
              information from children. If you become aware that a child has provided us with personal information,
              please contact us immediately.
            </p>
          </Section>

          <Section>
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting
              the updated policy on our website and updating the "Last updated" date. Your continued use of our services
              after such changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </Section>

          <Section>
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices,
              please contact us at:
            </p>
            <p>
              <a href={`mailto:${brand.contactEmail}`} style={{ color: brand.primaryColor }}>
                {brand.contactEmail}
              </a>
            </p>
          </Section>
        </ContentSection>

        <FooterSection>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
            {brand.footerLogo ? (
              <img src={brand.footerLogo} alt={brand.name} style={{ height: '30px', maxWidth: '150px', objectFit: 'contain', marginRight: '10px' }} />
            ) : brand.logo ? (
              <img src={brand.logo} alt={brand.name} style={{ height: '30px', maxWidth: '150px', objectFit: 'contain', marginRight: '10px' }} />
            ) : null}
          </div>
          <p>&copy; {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
          <p>
            Questions? Contact us at{' '}
            <a href={`mailto:${brand.contactEmail}`} style={{ color: brand.primaryColor }}>
              {brand.contactEmail}
            </a>
          </p>
        </FooterSection>
      </PageContainer>
    </ThemeProvider>
  );
};

export default PrivacyPage;
